"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoKeywords = exports.seoKeywordsResultSchema = exports.seoKeywordsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Keyword Analysis Action Types
var keywordsActions = [
    'GetKeywordOverview', // Get comprehensive metrics and analysis data for a single keyword
    'GetKeywordResults', // Get organic search results and rankings for a keyword
    'GetRelatedKeywords', // Get related keywords with relevance scores and metrics
    'GetRelatedSearchPhrases', // Get question-based keywords related to the main keyword
    'GetKeywordIdeas', // Get keyword ideas and suggestions with opportunity scoring
];
// Sort Types
var sortTypes = ['asc', 'desc'];
// Database Options
var databases = [
    'us',
    'uk',
    'ca',
    'ru',
    'de',
    'fr',
    'es',
    'it',
    'br',
    'au',
    'ar',
    'be',
    'ch',
    'dk',
    'fi',
    'hk',
    'ie',
    'il',
    'mx',
    'nl',
    'no',
    'pl',
    'se',
    'sg',
    'tr',
    'jp',
    'in',
];
exports.seoKeywordsArgsSchema = zod_1.z.object({
    action: zod_1.z
        .enum(keywordsActions)
        .describe('The type of keyword analysis to perform:\n' +
        '• GetKeywordOverview: Get comprehensive keyword metrics including search volume, CPC, competition, difficulty, and search intent. Perfect for keyword research and PPC campaign planning\n' +
        '• GetKeywordResults: Get organic search results showing which domains and URLs rank for a keyword. Ideal for competitor analysis and SERP research\n' +
        '• GetRelatedKeywords: Find semantically related keywords with relevance scores and metrics. Essential for content expansion and topic clustering\n' +
        '• GetRelatedSearchPhrases: Get question-based keywords related to your target keyword. Perfect for FAQ content creation and long-tail keyword discovery\n' +
        '• GetKeywordIdeas: Generate keyword ideas and suggestions with opportunity scoring based on volume, CPC, and difficulty metrics'),
    keyword: zod_1.z
        .string()
        .min(1)
        .max(200)
        .describe('The target keyword to analyze. MAXIMUM 3 WORDS for optimal search volume and traffic potential:\n' +
        '• Single keyword (e.g., "project management") - PREFERRED\n' +
        '• Two-word phrase (e.g., "marketing automation") - PREFERRED\n' +
        '• Three-word phrase (e.g., "ai marketing tools") - MAXIMUM\n' +
        '• Avoid longer phrases - they reduce search volume and traffic potential\n' +
        'Examples: "salesforce", "hubspot alternatives", "crm software"'),
    sortType: zod_1.z.enum(sortTypes).default('desc').describe('Sort order for results (default: desc)'),
    database: zod_1.z
        .enum(databases)
        .default('us')
        .describe('The geographic database to query (default: us)'),
    limit: zod_1.z
        .number()
        .min(1)
        .max(100)
        .default(10)
        .describe('Number of results to fetch (default 10, max 100). Keep at default unless user requests more data.'),
    language: zod_1.z
        .string()
        .default('English')
        .describe('Language for keyword ideas (default: English). Only used for GetKeywordIdeas action.'),
    location: zod_1.z
        .string()
        .default('United States')
        .describe('Location/country for keyword ideas (default: United States). Only used for GetKeywordIdeas action.'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if the search returns no results. Default to true.'),
    })
        .describe('Search options'),
});
// Unified column definitions for each action type
var columnDefinitions = zod_1.z.object({
    GetKeywordOverview: zod_1.z
        .object({
        keyword: zod_1.z.string().describe('Keyword phrase'),
        searchVolume: zod_1.z.number().describe('Monthly search volume'),
        costPerClick: zod_1.z.number().describe('Cost per click (CPC)'),
        competition: zod_1.z.number().describe('Competition level (0-1)'),
        numberOfResults: zod_1.z.number().describe('Number of search results'),
        trends: zod_1.z.string().optional().describe('Trend data (12-month search interest)'),
        searchIntent: zod_1.z
            .string()
            .describe('Search intent (0=commercial, 1=informational, 2=navigational, 3=transactional)'),
        keywordDifficulty: zod_1.z.number().describe('Keyword difficulty index (0-100)'),
    })
        .describe('Columns for GetKeywordOverview: comprehensive keyword metrics'),
    GetKeywordResults: zod_1.z
        .object({
        domain: zod_1.z.string().describe('Domain name'),
        url: zod_1.z.string().describe('URL of the ranking page'),
        position: zod_1.z.number().describe('Current position in search results'),
        title: zod_1.z.string().describe('Page title of the ranking result'),
        type: zod_1.z.string().describe('Result type (Organic, Ads, etc.)'),
    })
        .describe('Columns for GetKeywordResults: organic search rankings with metadata'),
    GetRelatedKeywords: zod_1.z
        .object({
        keyword: zod_1.z.string().describe('Related keyword phrase'),
        searchVolume: zod_1.z.number().describe('Monthly search volume'),
        costPerClick: zod_1.z.number().describe('Cost per click (CPC)'),
        competition: zod_1.z.number().describe('Competition level (0-1)'),
        numberOfResults: zod_1.z.number().describe('Number of search results'),
        trends: zod_1.z.string().optional().describe('Trend data (12-month search interest)'),
        relevanceScore: zod_1.z.number().describe('Related relevance score'),
        serpFeatures: zod_1.z.string().optional().describe('SERP features'),
        searchIntent: zod_1.z
            .string()
            .describe('Search intent (0=commercial, 1=informational, 2=navigational, 3=transactional)'),
        keywordDifficulty: zod_1.z.number().describe('Keyword difficulty index (0-100)'),
    })
        .describe('Columns for GetRelatedKeywords: related keywords with metrics'),
    GetRelatedSearchPhrases: zod_1.z
        .object({
        keyword: zod_1.z.string().describe('Question-based keyword phrase'),
        searchVolume: zod_1.z.number().describe('Monthly search volume'),
        costPerClick: zod_1.z.number().describe('Cost per click (CPC)'),
        competition: zod_1.z.number().describe('Competition level (0-1)'),
        numberOfResults: zod_1.z.number().describe('Number of search results'),
        trends: zod_1.z.string().optional().describe('Trend data (12-month search interest)'),
        searchIntent: zod_1.z
            .string()
            .describe('Search intent (0=commercial, 1=informational, 2=navigational, 3=transactional)'),
        keywordDifficulty: zod_1.z.number().describe('Keyword difficulty index (0-100)'),
    })
        .describe('Columns for GetRelatedSearchPhrases: question-based keywords'),
    GetKeywordIdeas: zod_1.z
        .object({
        keyword: zod_1.z.string().describe('Keyword suggestion'),
        keywordDifficulty: zod_1.z.number().describe('Keyword difficulty score (0-100)'),
        costPerClick: zod_1.z.number().describe('Cost per click (CPC)'),
        searchVolume: zod_1.z.number().describe('Average monthly search volume'),
        searchIntent: zod_1.z.string().describe('Search intent classification'),
        competition: zod_1.z
            .number()
            .describe('Competition level (0-1). Note: Returns -1 as placeholder when data unavailable from SEO Review Tools API'),
        opportunityScore: zod_1.z
            .number()
            .describe('Composite opportunity score based on volume, CPC, and difficulty'),
    })
        .describe('Columns for GetKeywordIdeas: keyword suggestions with opportunity scoring'),
});
exports.seoKeywordsResultSchema = zod_1.z.object({
    csvData: zod_1.z
        .string()
        .describe('Semicolon-delimited CSV containing all keyword research data. GetKeywordOverview: keyword, search volume, CPC, competition, number of results, trends, search intent, keyword difficulty (1 row). GetKeywordResults: domain, URL, position, title, type (multiple rows). GetRelatedKeywords: keyword, search volume, CPC, competition, number of results, trends, relevance score, SERP features, search intent, keyword difficulty (multiple rows). GetRelatedSearchPhrases: keyword, search volume, CPC, competition, number of results, trends, search intent, keyword difficulty (multiple rows). GetKeywordIdeas: keyword, keyword difficulty, CPC, search volume, search intent, competition, opportunity score (multiple rows).'),
    query: zod_1.z.object({
        action: zod_1.z.string().describe('The keyword analysis action that was performed'),
        keyword: zod_1.z.string().describe('The keyword that was analyzed'),
        database: zod_1.z.string().describe('The geographic database used'),
        limit: zod_1.z.number().describe('The limit parameter used'),
    }),
    columnSchema: zod_1.z
        .union([
        columnDefinitions.shape.GetKeywordOverview,
        columnDefinitions.shape.GetKeywordResults,
        columnDefinitions.shape.GetRelatedKeywords,
        columnDefinitions.shape.GetRelatedSearchPhrases,
        columnDefinitions.shape.GetKeywordIdeas,
        zod_1.z.record(zod_1.z.string()), // Allow simple key-value string pairs for backend compatibility
    ])
        .describe('Schema of the columns returned in csvData based on the action performed. Use this to understand the structure of each row in the CSV.'),
    metadata: zod_1.z.object({
        executedAt: zod_1.z.string().datetime(),
        recordCount: zod_1.z.number().optional().describe('Number of records returned'),
        actionType: zod_1.z
            .enum([
            'GetKeywordOverview',
            'GetKeywordResults',
            'GetRelatedKeywords',
            'GetRelatedSearchPhrases',
            'GetKeywordIdeas',
        ])
            .describe('The specific action type for column schema reference'),
    }),
});
exports.seoKeywords = (0, common_1.createEmitModeToolMetadata)({
    id: 'seoKeywords',
    displayName: 'Keyword Research',
    displayDescription: 'Analyze keyword data including search results, related keywords, question phrases, and difficulty metrics. Supports comprehensive keyword research for SEO strategy development.',
    category: 'seo',
    color: '#0066CC',
    backgroundColor: (0, common_1.createBgColor)('#0066CC'),
    icon: 'search',
    argsSchema: exports.seoKeywordsArgsSchema,
}, exports.seoKeywordsResultSchema);
